import { createSelector } from 'reselect';
import {
  calculateHRCAData,
  calculateHRPercentCAData,
  formatHRDashboardData,
  getCumulDashboardData,
} from 'helpers/HRhelpers';
import { selectUserCurrentCompanyExercice } from 'modules/loggedUserInfo/selectors';
import { selectCurrentCompanyFirstFiscalMonth } from 'modules/turnoverPage/selectors';
import {
  groupDatasetsByMonthsAndDays,
  mapDashboardDataToYearView,
} from 'helpers/chartDataMappers';
import { HR_TABS_IDS } from 'const/HRPage';

export const selectHRStatus = (state) => state.HRPage?.status;

export const selectHRLastUpdateDate = (state) =>
  state.HRPage?.lastUpdated || new Date(Date.now()).toISOString();

export const selectHRTotalHours = (state) => state.HRPage?.totalHoursData || [];

export const selectHRTotalCost = (state) => state.HRPage?.totalCostData || [];

export const selectHRTurnoverData = (state) => state.HRPage?.turnover || {};

export const selectHRTotalHoursForYearChart = createSelector(
  [
    selectHRTotalHours,
    selectUserCurrentCompanyExercice,
    selectCurrentCompanyFirstFiscalMonth,
  ],
  (totalHoursData, exercice, firstMonthName) =>
    formatHRDashboardData({
      exercice,
      firstMonthName,
      dashboardData: totalHoursData,
      datasetValueKey: 'totalheures',
    })
);

export const selectHRTotalCostForYearChart = createSelector(
  [
    selectHRTotalCost,
    selectUserCurrentCompanyExercice,
    selectCurrentCompanyFirstFiscalMonth,
  ],
  (totalCostData, exercice, firstMonthName) =>
    formatHRDashboardData({
      exercice,
      firstMonthName,
      dashboardData: totalCostData,
      datasetValueKey: 'totalcost',
    })
);

export const selectHRTurnoverDataForChart = createSelector(
  [selectHRTurnoverData],
  (turnoverData) =>
    groupDatasetsByMonthsAndDays(turnoverData.datasets, 'turnover')
);

export const selectHRTurnoverDataForYear = createSelector(
  [selectCurrentCompanyFirstFiscalMonth, selectHRTurnoverDataForChart],
  (firstMonthName, turnoverMonthsData) =>
    mapDashboardDataToYearView(turnoverMonthsData, firstMonthName)
);

export const selectHRCADataForYearChart = createSelector(
  [selectHRTurnoverDataForYear, selectHRTotalHoursForYearChart],
  (turnoverYearData, totalHoursYearData) =>
    calculateHRCAData({ turnoverYearData, totalHoursYearData })
);

export const selectHRPercentCADataForYearChart = createSelector(
  [selectHRTurnoverDataForYear, selectHRTotalCostForYearChart],
  (turnoverYearData, totalCostYearData) =>
    calculateHRPercentCAData({ turnoverYearData, totalCostYearData })
);

export const selectHRDashboardYearData = createSelector(
  [
    selectHRTotalCostForYearChart,
    selectHRTotalHoursForYearChart,
    selectHRCADataForYearChart,
    selectHRPercentCADataForYearChart,
  ],
  (totalCostData, totalHoursData, CAData, PercentCAData) => [
    { tabId: HR_TABS_IDS.TOTAL_COST, tabValues: totalCostData },
    { tabId: HR_TABS_IDS.TOTAL_HOURS, tabValues: totalHoursData },
    { tabId: HR_TABS_IDS.CA, tabValues: CAData },
    { tabId: HR_TABS_IDS.CA_PERCENTAGE, tabValues: PercentCAData },
  ]
);

export const selectCumulHRTurnoverData = createSelector(
  [selectHRTurnoverDataForYear],
  (turnoverData) => getCumulDashboardData(turnoverData)
);

export const selectCumulHRTotalCostData = createSelector(
  [selectHRTotalCostForYearChart],
  (totalCostData) => getCumulDashboardData(totalCostData)
);

export const selectCumulHRTotalHoursData = createSelector(
  [selectHRTotalHoursForYearChart],
  (totalHoursData) => getCumulDashboardData(totalHoursData)
);

export const selectCumulHRCADataForYearChart = createSelector(
  [selectCumulHRTurnoverData, selectCumulHRTotalHoursData],
  (cumulTurnoverYearData, cumulTotalHoursYearData) =>
    calculateHRCAData({
      turnoverYearData: cumulTurnoverYearData,
      totalHoursYearData: cumulTotalHoursYearData,
      uniqueDatasetIndex: 'cumul',
    })
);

export const selectCumulHRPercentCADataForYearChart = createSelector(
  [selectCumulHRTurnoverData, selectCumulHRTotalCostData],
  (cumulTurnoverYearData, cumulTotalCostYearData) =>
    calculateHRPercentCAData({
      turnoverYearData: cumulTurnoverYearData,
      totalCostYearData: cumulTotalCostYearData,
      uniqueDatasetIndex: 'cumul',
    })
);

export const selectCAYearTotal = createSelector(
  [selectCumulHRCADataForYearChart],
  (yearsValues) => {
    const mostRecentYearData = yearsValues.reduce((acc, data) => {
      if (!acc.year || Number(acc.year) < Number(data.year)) {
        return data;
      }
      return acc;
    }, {});

    if (!mostRecentYearData.values) {
      return {};
    }

    const relevantPeriodIndex = mostRecentYearData.values.reduce(
      (acc, value, index) => (value ? index : acc),
      0
    );

    return yearsValues.reduce((acc, { year, values }) => {
      acc[year] = values[relevantPeriodIndex];
      return acc;
    }, {});
  }
);

export const selectCumulHRDashboardYearData = createSelector(
  [
    selectCumulHRTotalCostData,
    selectCumulHRTotalHoursData,
    selectCumulHRCADataForYearChart,
    selectCumulHRPercentCADataForYearChart,
  ],
  (
    cumulTotalCostData,
    cumulTotalHoursData,
    cumulCAData,
    cumulPercentCAData
  ) => [
    { tabId: HR_TABS_IDS.TOTAL_COST, tabValues: cumulTotalCostData },
    { tabId: HR_TABS_IDS.TOTAL_HOURS, tabValues: cumulTotalHoursData },
    { tabId: HR_TABS_IDS.CA, tabValues: cumulCAData },
    { tabId: HR_TABS_IDS.CA_PERCENTAGE, tabValues: cumulPercentCAData },
  ]
);
